import type {
    ComponentChildren,
    JSX,
} from "preact";
import {
    forwardRef,
} from "preact/compat";

type TableProps = JSX.HTMLAttributes<HTMLTableElement> & {
    children: ComponentChildren;
};

export const Table = forwardRef<HTMLTableElement, TableProps>(
    ({
        className = "",
        children,
        ...props
    }, ref) => {
        return (
            <div className="overflow-x-auto">
                <table
                    ref={ref}
                    className={`w-full text-sm ${className}`}
                    {...props}
                >
                    {children}
                </table>
            </div>
        );
    },
);

type TableHeaderProps = JSX.HTMLAttributes<HTMLTableSectionElement> & {
    children: ComponentChildren;
};

export const TableHeader = forwardRef<
    HTMLTableSectionElement,
    TableHeaderProps
>(
    ({
        className = "",
        children,
        ...props
    }, ref) => {
        return (
            <thead
                ref={ref}
                className={`border-b border-border ${className}`}
                {...props}
            >
                {children}
            </thead>
        );
    },
);

type TableBodyProps = JSX.HTMLAttributes<HTMLTableSectionElement> & {
    children: ComponentChildren;
};

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
    ({
        className = "",
        children,
        ...props
    }, ref) => {
        return (
            <tbody
                ref={ref}
                className={className}
                {...props}
            >
                {children}
            </tbody>
        );
    },
);

type TableRowProps = JSX.HTMLAttributes<HTMLTableRowElement> & {
    children: ComponentChildren;
};

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
    ({
        className = "",
        children,
        ...props
    }, ref) => {
        return (
            <tr
                ref={ref}
                className={`border-b border-border last:border-b-0 hover:bg-bg-tertiary transition-colors ${className}`}
                {...props}
            >
                {children}
            </tr>
        );
    },
);

type TableHeadProps = JSX.ThHTMLAttributes<HTMLTableCellElement> & {
    children?: ComponentChildren;
};

export const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
    ({
        className = "",
        children,
        ...props
    }, ref) => {
        return (
            <th
                ref={ref}
                className={`px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider ${className}`}
                {...props}
            >
                {children}
            </th>
        );
    },
);

type TableCellProps = JSX.TdHTMLAttributes<HTMLTableCellElement> & {
    children?: ComponentChildren;
};

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
    ({
        className = "",
        children,
        ...props
    }, ref) => {
        return (
            <td
                ref={ref}
                className={`px-4 py-3 text-text-primary ${className}`}
                {...props}
            >
                {children}
            </td>
        );
    },
);
